import { ErpSyncFailureCategory } from './erp-sync-failure-category.enum';

type FailureCategoryMatcher = {
    category: ErpSyncFailureCategory;
    patterns: (string | RegExp)[];
};

/**
 * Known ERP sync failure patterns based on common issues documented in Confluence.
 * Patterns are matched against error messages to categorize failures.
 * See: https://40seas.atlassian.net/wiki/spaces/40SEAS/pages/862748673/ERP+Sync+Common+Issue
 *
 * Categories are ordered by specificity - more specific patterns (e.g., Portless, CreditMemo)
 * should be checked before generic ones (e.g., NonSucceededPayments).
 */
const FAILURE_CATEGORY_MATCHERS: FailureCategoryMatcher[] = [
    // Check Xero-specific patterns first to avoid matching with generic Rutter patterns
    {
        category: ErpSyncFailureCategory.XeroRateLimitOnAttachments,
        patterns: ['rate limit error on platform xero', 'xero.*rate limit', /rate.*limit.*xero/, /xero.*rate.*limit/],
    },
    {
        category: ErpSyncFailureCategory.RutterRateLimitError,
        patterns: [
            'rate limit error on platform quickbooks',
            'rate limit error on platform rutter',
            'rutter.*rate limit',
            /rate.*limit.*error.*platform.*(?:quickbooks|rutter)/,
            /external_api_rate_limit_error/,
        ],
    },
    {
        category: ErpSyncFailureCategory.RutterUnsyncedLineItem,
        patterns: ['unsynced line item', 'line item.*not synced', 'line item stuck', /line.*item.*pending/],
    },
    {
        category: ErpSyncFailureCategory.RutterInvoiceAlreadyFullyPaid,
        patterns: [
            'not payable since it has been fully paid',
            'invoice.*not payable.*fully paid',
            /invoice.*is not payable since it has been fully paid/,
            /invoice.*fully paid/,
        ],
    },
    {
        category: ErpSyncFailureCategory.RutterDuplicateExternalInvoiceIds,
        patterns: ['duplicate.*external.*invoice', 'same externalid', 'duplicate invoice id', /duplicate.*invoice.*id/],
    },
    {
        category: ErpSyncFailureCategory.PortlessNonSucceededPayments,
        patterns: ['portless.*non.*succeeded', 'portless.*payment', /portless/],
    },
    {
        category: ErpSyncFailureCategory.CreditMemoNonSucceededPayments,
        patterns: ['credit memo.*non.*succeeded', 'credit memo.*payment', 'credit.*memo', /credit.*memo/],
    },
    {
        category: ErpSyncFailureCategory.NonSucceededPayments,
        patterns: ['non-succeeded payments', 'non succeeded payments', /non.*succeeded.*payment/],
    },
];

/**
 * Categorizes an ERP sync failure based on the error message.
 * Matches against known issue patterns and returns the category, or Unknown as fallback.
 * Use this when setting status to Failed or Blocked so the event payload includes errorCategory.
 */
export function categorizeErpSyncFailure(errorMessage?: string): ErpSyncFailureCategory {
    if (!errorMessage) {
        return ErpSyncFailureCategory.Unknown;
    }

    const normalizedMessage = errorMessage.toLowerCase();

    for (const matcher of FAILURE_CATEGORY_MATCHERS) {
        for (const pattern of matcher.patterns) {
            if (typeof pattern === 'string') {
                if (normalizedMessage.includes(pattern.toLowerCase())) {
                    return matcher.category;
                }
            } else if (pattern instanceof RegExp) {
                if (pattern.test(normalizedMessage)) {
                    return matcher.category;
                }
            }
        }
    }

    return ErpSyncFailureCategory.Unknown;
}
